import type { CreateDocumentBody, ObraDocumentsResult, UpdateDocumentBody } from '@obras/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

export const documentosKey = (obraId: string) => ['obras', obraId, 'documentos'] as const;

/**
 * Mexer no dossiê mexe na obra.
 *
 * Quem anexa entra na equipe como auxiliar — é o servidor que faz isso. Se só
 * a lista de anexos fosse recarregada, a aba "Equipe" continuaria mostrando o
 * quadro de antes até alguém navegar de novo, e a pessoa não se veria lá.
 */
function recarregarObra(queryClient: ReturnType<typeof useQueryClient>, obraId: string): void {
  void queryClient.invalidateQueries({ queryKey: documentosKey(obraId) });
  // A chave do detalhe é `['obras', 'detail', id]` — ver `obraKeys` em obras/api.
  void queryClient.invalidateQueries({ queryKey: ['obras', 'detail', obraId] });
}

export function useDocuments(obraId: string) {
  return useQuery({
    queryKey: documentosKey(obraId),
    queryFn: ({ signal }) => api.get<ObraDocumentsResult>(`/obras/${obraId}/documentos`, signal),
  });
}

export function useCreateDocument(obraId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateDocumentBody) =>
      api.post<{ id: string }>(`/obras/${obraId}/documentos`, body),
    onSuccess: () => recarregarObra(queryClient, obraId),
  });
}

export function useUpdateDocument(obraId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: UpdateDocumentBody }) =>
      api.patch<{ id: string }>(`/obras/${obraId}/documentos/${documentId}`, body),
    onSuccess: () => recarregarObra(queryClient, obraId),
  });
}

export function useDeleteDocument(obraId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      api.delete<void>(`/obras/${obraId}/documentos/${documentId}`),
    onSuccess: () => recarregarObra(queryClient, obraId),
  });
}
